/**
 * Turns a skin's chrome spec (menus, toolbars, module order) into the
 * items the classic shell renders, against the same nav model and action
 * registry the modern shell uses. Pure, so the mapping is unit-tested.
 *
 * Unknown ids (module not installed, action not registered) are dropped.
 * A registered action that is disabled or not permitted is dropped in
 * Velora Modern but kept greyed in the familiar modes (`showDisabled`),
 * with its reason as the tooltip (P1-20, DTX-24): the real products grey a
 * verb that does not apply, so a missing item reads as "broken".
 * Separators collapse so no menu or toolbar starts, ends or doubles one.
 */
import { isTermKey, type TermKey } from '@velora/contracts/skins/terms';
import { actionState, canonicalAction, type ShellActionDef } from '../actions/registry.js';
import type { MenuEntry } from '@velora/ui';
import type { ReactNode } from 'react';
import type { ToolbarLayout } from '@velora/contracts/shell';
import type {
  ChromeContext as SkinChromeContext,
  ClassicChrome,
  CounterRef,
  MenuItemSpec,
  SkinLabel,
  ToolbarEntry,
} from '../skins/types.js';

/** One nav item as the shell sees it (after permission and terms). */
export interface NavEntry {
  /** `<moduleId>:<routeId>` */
  id: string;
  moduleId: string;
  label: string;
  icon: ReactNode;
  href: string;
  badge?: number;
  locked?: boolean;
}

/** An action ready to show: label already in the skin's wording. */
export interface ResolvedAction {
  id: string;
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  run: () => void;
  /** Set on what `inactiveAction` returns: why it is greyed. */
  disabledReason?: string;
}

export interface ChromeContext {
  nav: readonly NavEntry[];
  /** The skin's module list (nav ids), in order. */
  moduleOrder: readonly string[];
  action: (id: string) => ResolvedAction | undefined;
  /**
   * A registered action that `action` refuses (disabled, or not permitted):
   * what a `showDisabled` toolbar shows greyed. Unregistered ids: undefined.
   */
  inactiveAction?: (id: string) => ResolvedAction | undefined;
  /**
   * Keep inactive actions greyed on menus and toolbars (every familiar
   * mode); false drops them (Velora Modern). Default false.
   */
  showDisabled?: boolean;
  commandsInGroup: (group: string) => readonly ResolvedAction[];
  term: (key: TermKey) => string;
  navigate: (href: string) => void;
}

/** A label that may be a term key or literal text. */
export function labelText(label: SkinLabel, term: (key: TermKey) => string): string {
  return isTermKey(label) ? term(label) : label;
}

/** `patients` matches the module's first nav item; `patients:list` that exact item. */
export function findNav(nav: readonly NavEntry[], ref: string): NavEntry | undefined {
  return ref.includes(':') ? nav.find((n) => n.id === ref) : nav.find((n) => n.moduleId === ref);
}

/** The skin's modules in its order, and every other nav item (Velora extras). */
export function splitModules(
  nav: readonly NavEntry[],
  order: readonly string[],
): { primary: NavEntry[]; extras: NavEntry[] } {
  const primary: NavEntry[] = [];
  for (const ref of order) {
    const hit = findNav(nav, ref);
    if (hit && !primary.includes(hit)) primary.push(hit);
  }
  return { primary, extras: nav.filter((n) => !primary.includes(n)) };
}

/** The nav entry behind `nav.<module>` or `nav.<module>.<route>`, if it is in the nav. */
function navEntryFor(ctx: ChromeContext, requested: string): NavEntry | undefined {
  const id = canonicalAction(requested);
  if (!id.startsWith('nav.')) return undefined;
  const [moduleId, routeId] = id.slice(4).split('.');
  if (!moduleId) return undefined;
  return findNav(ctx.nav, routeId ? `${moduleId}:${routeId}` : moduleId);
}

/** `nav.<module>` or `nav.<module>.<route>` as a nav entry action. */
function navAction(ctx: ChromeContext, requested: string): ResolvedAction | undefined {
  const entry = navEntryFor(ctx, requested);
  if (!entry) return undefined;
  return {
    id: requested,
    label: entry.label,
    icon: entry.icon,
    run: () => ctx.navigate(entry.href),
  };
}

export function resolveAction(ctx: ChromeContext, id: string): ResolvedAction | undefined {
  return navAction(ctx, id) ?? ctx.action(id);
}

/**
 * An id as the chrome shows it: live, greyed (registered but disabled or
 * forbidden, only when `showDisabled`), or nothing (unknown, or greyed
 * items are hidden).
 */
function resolveForChrome(
  ctx: ChromeContext,
  id: string,
  showDisabled: boolean,
): { action: ResolvedAction; live: boolean } | undefined {
  const live = resolveAction(ctx, id);
  if (live) return { action: live, live: true };
  if (!showDisabled) return undefined;
  const grey = ctx.inactiveAction?.(id);
  return grey ? { action: grey, live: false } : undefined;
}

function tidy<T>(items: readonly T[], isSep: (t: T) => boolean): T[] {
  const out: T[] = [];
  for (const item of items) {
    if (isSep(item) && (out.length === 0 || isSep(out[out.length - 1] as T))) continue;
    out.push(item);
  }
  while (out.length > 0 && isSep(out[out.length - 1] as T)) out.pop();
  return out;
}

/** Suffix of a locked add-on in the familiar menus ("Reviews (add-on)"). */
export const ADD_ON_SUFFIX = ' (add-on)';

function navEntriesToMenu(entries: readonly NavEntry[], ctx: ChromeContext): MenuEntry[] {
  return entries.map((n) => ({
    id: `nav:${n.id}`,
    label: n.locked ? `${n.label}${ADD_ON_SUFFIX}` : n.label,
    icon: n.icon,
    ...(n.locked ? { muted: true, title: 'Add-on: opens what it does and how to add it' } : {}),
    onSelect: () => ctx.navigate(n.href),
  }));
}

export function resolveMenuItems(
  items: readonly MenuItemSpec[],
  ctx: ChromeContext,
  prefix = 'm',
): MenuEntry[] {
  const out: MenuEntry[] = [];
  const { primary, extras } = splitModules(ctx.nav, ctx.moduleOrder);
  // A page a menu names itself (the Velora menu's first block) is not listed
  // again by an `expand` further down the same menu.
  const named = new Set<string>();
  for (const item of items) {
    if (item === '-' || (typeof item !== 'string' && !('action' in item))) continue;
    const entry = navEntryFor(ctx, typeof item === 'string' ? item : item.action);
    if (entry) named.add(entry.id);
  }
  const fresh = (list: readonly NavEntry[]) => list.filter((n) => !named.has(n.id));
  items.forEach((item, i) => {
    const key = `${prefix}-${i}`;
    if (item === '-') {
      out.push({ type: 'separator', id: key });
      return;
    }
    if (typeof item === 'string' || 'action' in item) {
      const id = typeof item === 'string' ? item : item.action;
      const hit = resolveForChrome(ctx, id, ctx.showDisabled ?? false);
      if (!hit) return;
      const a = hit.action;
      const label =
        typeof item !== 'string' && item.label ? labelText(item.label, ctx.term) : a.label;
      out.push({
        id: `${key}-${id}`,
        label,
        ...(a.icon ? { icon: a.icon } : {}),
        ...(a.shortcut ? { shortcut: [a.shortcut] } : {}),
        ...(hit.live
          ? { onSelect: a.run }
          : { disabled: true, title: a.disabledReason ?? '', onSelect: () => {} }),
      });
      return;
    }
    if ('expand' in item) {
      const what = item.expand;
      if (what === 'modules') out.push(...navEntriesToMenu(fresh(primary), ctx));
      else if (what === 'extras') out.push(...navEntriesToMenu(fresh(extras), ctx));
      else if (what === 'all-modules')
        out.push(...navEntriesToMenu(fresh([...primary, ...extras]), ctx));
      else {
        for (const a of ctx.commandsInGroup(what.slice('commands:'.length))) {
          out.push({ id: `${key}-${a.id}`, label: a.label, onSelect: a.run });
        }
      }
      return;
    }
    const sub = resolveMenuItems(item.items, ctx, key);
    if (sub.length > 0) {
      out.push({ type: 'submenu', id: key, label: labelText(item.submenu, ctx.term), items: sub });
    }
  });
  return tidy(out, (e) => e.type === 'separator');
}

export interface ResolvedToolbarButton {
  id: string;
  label: string;
  icon: ReactNode;
  shortcut?: string;
  onSelect: () => void;
  badge?: CounterRef;
  /** Label beside the icon even on an icon-only toolbar (the schedule button). */
  showLabel?: boolean;
  /** Shown greyed (a `showDisabled` toolbar's disabled or forbidden action). */
  disabled?: boolean;
  /** The greyed button's tooltip. */
  disabledReason?: string;
}

export type ResolvedToolbarItem = ResolvedToolbarButton | { id: string; separator: true };

export function resolveToolbar(
  entries: readonly ToolbarEntry[],
  ctx: ChromeContext,
  renderIcon: (icon: NonNullable<Exclude<ToolbarEntry, string>['icon']>) => ReactNode,
  options: { showDisabled?: boolean } = {},
): ResolvedToolbarItem[] {
  const out: ResolvedToolbarItem[] = [];
  entries.forEach((entry, i) => {
    if (entry === '-') {
      out.push({ id: `sep-${i}`, separator: true });
      return;
    }
    const id = typeof entry === 'string' ? entry : entry.action;
    const hit = resolveForChrome(ctx, id, options.showDisabled ?? ctx.showDisabled ?? false);
    if (!hit) return;
    const a = hit.action;
    const spec = typeof entry === 'string' ? undefined : entry;
    out.push({
      id,
      label: spec?.label ? labelText(spec.label, ctx.term) : a.label,
      icon: spec?.icon ? renderIcon(spec.icon) : a.icon,
      ...(a.shortcut ? { shortcut: a.shortcut } : {}),
      ...(spec?.badge ? { badge: spec.badge } : {}),
      ...(spec?.showLabel ? { showLabel: true } : {}),
      ...(hit.live ? {} : { disabled: true, disabledReason: a.disabledReason ?? '' }),
      onSelect: hit.live ? a.run : () => {},
    });
  });
  return tidy(out, (e) => 'separator' in e);
}

/**
 * A user's toolbar layout over the mode's toolbar: hidden buttons go,
 * `order` (when given) sorts the buttons that remain while group gaps stay
 * where the mode put them relative to the first button after them.
 */
export function applyToolbarLayout(
  items: readonly ResolvedToolbarItem[],
  layout: ToolbarLayout | undefined,
): ResolvedToolbarItem[] {
  if (!layout) return [...items];
  const hidden = new Set(layout.hidden);
  const kept = items.filter((i) => 'separator' in i || !hidden.has(i.id));
  const order = layout.order;
  if (!order || order.length === 0) return tidy(kept, (e) => 'separator' in e);
  const rank = new Map(order.map((id, i) => [id, i]));
  const buttons = kept.filter((i): i is ResolvedToolbarButton => !('separator' in i));
  const sorted = [...buttons].sort(
    (a, b) =>
      (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
  // Ordered by the user: one flat group, the mode's gaps no longer apply.
  return sorted;
}

/** The title strip text (Dentrix "Patient Chart - Davis, Karen [DA0003]", OD's format). */
export function titleText(chrome: ClassicChrome, ctx: SkinChromeContext): string {
  if (chrome.titleStrip.format) return chrome.titleStrip.format(ctx);
  const show = chrome.titleStrip.showPatient ?? true;
  const withPatient = Array.isArray(show) ? !show.includes(ctx.moduleId ?? '') : show;
  if (!withPatient || !ctx.patient) return ctx.title;
  const chart = ctx.patient.chartNumber ? ` [${ctx.patient.chartNumber}]` : '';
  return `${ctx.title} - ${ctx.patient.label}${chart}`;
}

/** A module bar tile's href: the patient route while a patient is active (OD keeps the patient). */
export function tileHref(
  chrome: ClassicChrome,
  navId: string,
  moduleId: string,
  fallback: string,
  patientId: string | null,
): string {
  if (!patientId) return fallback;
  const routes = chrome.moduleBar.patientRoutes;
  const route = routes?.[navId] ?? routes?.[moduleId];
  return route ? route.replace('$patientId', encodeURIComponent(patientId)) : fallback;
}

/* --------------------------------------------------- right-click menus */

export interface ContextMenuDeps {
  /** The action (or palette command) behind an id, aliases resolved. */
  find: (id: string) => ShellActionDef | undefined;
  term: (key: TermKey) => string;
  /** Whether the person holds `permission` in the current office scope. */
  can: (permission: string) => boolean;
  /** Keep disabled or forbidden actions greyed with their reason (familiar modes). */
  showDisabled?: boolean;
}

function stripAccessKey(label: string): string {
  return label.replace(/&(?=\S)/g, '');
}

/**
 * A mode's right-click menu (`ScheduleSkinOptions.apptMenu` / `slotMenu`,
 * chart and ledger row menus) against the action registry, with the same
 * rules as the menu bar: unknown ids are dropped; disabled or forbidden ones
 * are greyed with the reason as the tooltip when `showDisabled`, else
 * dropped; `&` access keys are removed (a context menu has none); generated
 * `expand` entries belong to the menu bar and are ignored here.
 */
export function resolveContextMenu(
  items: readonly MenuItemSpec[],
  deps: ContextMenuDeps,
  prefix = 'cm',
): MenuEntry[] {
  const out: MenuEntry[] = [];
  items.forEach((item, i) => {
    const key = `${prefix}-${i}`;
    if (item === '-') {
      out.push({ type: 'separator', id: key });
      return;
    }
    if (typeof item === 'string' || 'action' in item) {
      const id = typeof item === 'string' ? item : item.action;
      const a = deps.find(id);
      if (!a) return;
      const state = actionState(a, deps.can);
      if (!state.active && !deps.showDisabled) return;
      const own = typeof item !== 'string' ? item.label : undefined;
      const label = own ? labelText(own, deps.term) : a.labelKey ? deps.term(a.labelKey) : a.label;
      out.push({
        id: `${key}-${id}`,
        label: stripAccessKey(label),
        ...(state.active
          ? { onSelect: a.run }
          : { disabled: true, title: state.reason, onSelect: () => {} }),
      });
      return;
    }
    if ('submenu' in item) {
      const sub = resolveContextMenu(item.items, deps, key);
      if (sub.length === 0) return;
      out.push({
        type: 'submenu',
        id: key,
        label: stripAccessKey(labelText(item.submenu, deps.term)),
        items: sub,
      });
    }
  });
  return tidy(out, (e) => e.type === 'separator');
}
